import styled from '@emotion/styled'
import { ApiLandingPageVideo, ApiVideo } from '@storefronts/common-types'
import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { MEDIA_BORDER_RADIUS_VAR } from '../../../../context/CustomizationContextProvider'
import { Constants } from '../../../../style/styleConstants'

type AnimatedThumbnailProps = {
    animated: boolean
    animatedUrl: string
    staticUrl: string | null
    onClick?: () => void
    thumbnailStyle?: React.CSSProperties
    video?: Pick<ApiLandingPageVideo | ApiVideo, 'aspectRatio'>
    priority?: boolean
    shouldPreload?: boolean
}

const ThumbnailContainer = styled.div<{ isSquare: boolean }>(
    {
        width: '100%',
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'transparent',
        borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
        height: '100%',
    },
    (props) => ({
        aspectRatio: props.isSquare ? '1/1' : '5/7',
    }),
)

export const AnimatedThumbnail: React.FC<AnimatedThumbnailProps> = ({
    animated,
    staticUrl,
    animatedUrl,
    onClick,
    thumbnailStyle = {},
    video,
    priority = false,
    shouldPreload = false,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const el = videoRef.current
        if (!el) return

        if (animated) {
            el.play().catch(() => {})
        } else {
            el.pause()
            el.currentTime = 0
        }
    }, [animated])

    const isSquare = video?.aspectRatio === '1:1'
    const objectFit = 'cover'

    return (
        <ThumbnailContainer isSquare={isSquare} onClick={onClick}>
            <div
                style={{
                    borderRadius: 'inherit',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                }}
            >
                {animatedUrl && (
                    <video
                        ref={videoRef}
                        style={{
                            borderRadius: 'inherit',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: animated ? -1 : -3,
                            pointerEvents: 'none',
                            height: '100%',
                            objectFit,
                            ...thumbnailStyle,
                        }}
                        width={'100%'}
                        height={'100%'}
                        playsInline={true}
                        muted={true}
                        loop={true}
                        autoPlay={animated}
                        preload={shouldPreload || animated ? 'auto' : 'none'}
                        src={animatedUrl}
                    />
                )}

                {staticUrl && (
                    <Image
                        sizes={`(max-width: ${Constants.EXPANDED_BREAKPOINT}px) 70vw, ${Math.round(Constants.CONTENT_BLOCK_DESKTOP_WIDTH * 0.68)}px`}
                        priority={priority}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            borderRadius: 'inherit',
                            zIndex: -2,
                            objectFit,
                            ...thumbnailStyle,
                        }}
                        fill={true}
                        src={staticUrl}
                        alt=""
                    />
                )}
            </div>
        </ThumbnailContainer>
    )
}
